<script setup lang="ts">
import { QItem, QSelect, QVirtualScroll } from 'quasar'
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue'
import { Color } from '@previo/codebooks'
import type { PValidationRule } from '../../types/types'
import { $t } from '../../composables/i18n'
import { usePrevioDS } from '../../composables/helpers'
import { useHandleScrollQSelect } from '../../composables/menu/handleScrollQSelect'
import PCard from '../card/PCard.vue'
import { PCardSize } from '../card/types'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import { PMaxLines } from '../typography/types'
import { menuOffset } from './composable/menuOffset'
import InputFieldLabel from './components/LabelInputField.vue'
import Chip from './components/Chip.vue'
import CreateBtn from './components/CreateBtn.vue'
import MobileSuggestionDialog from './components/MobileSuggestionDialog.vue'
import PCheckbox from './PCheckbox.vue'
import PTextInputField from './PTextInputField.vue'
import { PInputAutofocusType } from './types'

const WHOLE_STRING_INPUT_TYPES = ['insertFromPaste', 'insertFromDrop', 'insertReplacementText']

const model = defineModel<string[]>({ default: () => [] })

const slots = useSlots()

type Props = {
  label?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  itemRules?: PValidationRule[]
  tooltip?: null | string
  autofocus?: boolean
  autofocusType?: null | PInputAutofocusType
  errorMessage?: null | string
  items?: string[]
  noData?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  label: null,
  placeholder: null,
  hint: null,
  tooltip: null,
  autofocus: false,
  autofocusType: PInputAutofocusType.INPUT,
  errorMessage: null,
  noData: null,
  disable: false,
  readonly: false,
  debounce: 0,
  disableClearable: false,
  items() {
    return []
  },
  rules() {
    return []
  },
  itemRules() {
    return []
  },
})

type Emits = {
  (e: 'blur'): void
}

const emit = defineEmits<Emits>()

const ds = usePrevioDS()

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)

const inputValue = ref<string | null>(null)
const itemError = ref<string | null>(null)
const isMobileDialogShow = ref<boolean>(false)

// Hack: Quasar emit `@popup-show` on QSelect after call `removeAtIndex` function in `#selected-item` slot. This is solution how to prevent it
const isOpenMobileDialogIgnored = ref<boolean>(false)

const dialogOwnItems = ref<string[]>([])

const quasarModel = computed<string[] | null>({
  get() {
    return model.value
  },
  set(value) {
    model.value = value ?? []
  },
})

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const isModelEmpty = computed<boolean>(() => {
  return model.value.length === 0
})

const isEnableClearable = computed<boolean>(() => {
  if (props.disableClearable || isRequired.value) {
    return false
  }

  return !isModelEmpty.value
})

const activeErrorMessage = computed<string | null>(() => {
  return itemError.value ?? props.errorMessage
})

const hasAnyErrors = computed<boolean>(() => {
  return !!activeErrorMessage.value
})

const filterByInput = (items: string[]): string[] => {
  if (inputValue.value === null || inputValue.value === '') {
    return items
  }

  const needle = inputValue.value.toLowerCase()

  return items.filter((item) => item.toLowerCase().includes(needle))
}

const itemsFiltered = computed<string[]>(() => {
  return filterByInput(props.items)
})

const mobileItems = computed<string[]>(() => {
  const ownItems = dialogOwnItems.value.filter((value) => !props.items.includes(value))

  return filterByInput([...props.items, ...ownItems])
})

const trimmedInput = computed<string>(() => {
  return (inputValue.value ?? '').trim()
})

const hasExactMatch = computed<boolean>(() => {
  const needle = trimmedInput.value.toLowerCase()
  if (needle.length === 0) {
    return false
  }

  return [...props.items, ...model.value].some((item) => item.toLowerCase() === needle)
})

const canAddOwnText = computed<boolean>(() => {
  return trimmedInput.value.length > 0 && !hasExactMatch.value
})

const changeInputValue = (value: string | number | null | undefined): void => {
  inputValue.value = value == null || value === '' ? null : value.toString()
  itemError.value = null
}

const validateItem = (value: string): string | null => {
  for (const rule of props.itemRules) {
    const result = rule(value)
    if (result !== true) {
      return typeof result === 'string' ? result : $t('Invalid value')
    }
  }

  return null
}

const isAlreadySelected = (value: string): boolean => {
  return model.value.some((selected) => selected.toLowerCase() === value.toLowerCase())
}

const showOwnItemInMobileDialog = (value: string): void => {
  if (!isMobileDialogShow.value) {
    return
  }

  if (props.items.includes(value) || dialogOwnItems.value.includes(value)) {
    return
  }

  dialogOwnItems.value = [...dialogOwnItems.value, value]
}

const clearInput = (): void => {
  inputValue.value = null
  qSelectComponent.value?.updateInputValue('')
}

const addValue = (value: string | null | undefined): void => {
  if (props.disable) {
    return
  }

  const trimmed = (value ?? '').trim()
  if (trimmed.length === 0) {
    return
  }

  itemError.value = validateItem(trimmed)
  if (itemError.value !== null) {
    return
  }

  if (!isAlreadySelected(trimmed)) {
    model.value = [...model.value, trimmed]
  }

  showOwnItemInMobileDialog(trimmed)
  clearInput()
}

const onBlur = (): void => {
  emit('blur')

  const rejectedInput = itemError.value === null ? null : inputValue.value
  if (rejectedInput === null) {
    return
  }

  // QSelect resets its input on focusout; the rejected value must survive so the user can fix it
  nextTick(() => {
    qSelectComponent.value?.updateInputValue(rejectedInput, true)
    itemError.value = validateItem(rejectedInput.trim())
  })
}

const onNativeChange = (event: Event): void => {
  const target = event.target
  if (!(target instanceof HTMLInputElement)) {
    return
  }

  addValue(target.value)
}

const onNativeInput = (event: Event): void => {
  if (!(event instanceof InputEvent) || !(event.target instanceof HTMLInputElement)) {
    return
  }

  if (!WHOLE_STRING_INPUT_TYPES.includes(event.inputType)) {
    return
  }

  addValue(event.target.value)
}

const isSelected = (item: string): boolean => {
  return model.value.includes(item)
}

const toggleItem = (item: string): void => {
  if (model.value.includes(item)) {
    model.value = model.value.filter((selected) => selected !== item)
  } else {
    model.value = [...model.value, item]
  }
}

const removeChip = (index: number, removeAtIndex: (index: number) => void): void => {
  if (props.readonly) {
    return
  }

  isOpenMobileDialogIgnored.value = true
  removeAtIndex(index)
}

const onMenuShow = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    if (props.items.length === 0) {
      qSelectComponent.value?.hidePopup()

      return
    }

    handleScrollQSelect.subscribe()

    return
  }

  qSelectComponent.value?.hidePopup()

  if (isOpenMobileDialogIgnored.value) {
    isOpenMobileDialogIgnored.value = false
  } else {
    dialogOwnItems.value = model.value.filter((value) => !props.items.includes(value))
    isMobileDialogShow.value = true
  }
}

const onMenuHide = (): void => {
  if (ds.screen().isLaptopAndLarge) {
    handleScrollQSelect.unsubscribe()
  }
}

const showPopup = (): void => {
  if (ds.screen().isMobile) {
    isMobileDialogShow.value = true

    return
  }

  qSelectComponent.value?.showPopup()
}

onMounted(() => {
  if (props.autofocus && props.autofocusType === PInputAutofocusType.DROPDOWN) {
    showPopup()
  }
})

watch(model, () => {
  qSelectComponent.value?.updateInputValue('')
})

watch(isMobileDialogShow, (isShown) => {
  if (!isShown) {
    dialogOwnItems.value = []
    inputValue.value = null
    itemError.value = null
  }
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    v-model="quasarModel"
    popup-no-route-dismiss
    :rules="rules"
    lazy-rules
    :input-debounce="debounce ?? 0"
    :placeholder="
      ds.screen().isLaptopAndLarge && isModelEmpty ? placeholder ?? undefined : undefined
    "
    :hint="hint ?? undefined"
    :options="itemsFiltered"
    :error="hasAnyErrors"
    :error-message="activeErrorMessage ?? undefined"
    :autofocus="autofocus"
    autocomplete="nope"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    :bg-color="Color.WHITE"
    dropdown-icon="fa-light fa-chevron-down"
    clear-icon="fa-light fa-xmark"
    :clearable="isEnableClearable"
    behavior="menu"
    :menu-offset="menuOffset"
    :use-input="ds.screen().isLaptopAndLarge"
    outlined
    no-error-icon
    hide-bottom-space
    use-chips
    multiple
    :data-cy="cy"
    @new-value="addValue"
    @input-value="changeInputValue"
    @popup-show="onMenuShow"
    @popup-hide="onMenuHide"
    @change="onNativeChange"
    @input="onNativeInput"
    @blur="onBlur"
  >
    <template v-if="slots['options-header']" #before-options>
      <slot name="options-header" />
    </template>

    <template v-if="ds.screen().isMobile && isModelEmpty" #prepend>
      <p-text-form-body :text="placeholder" :color="Color.GREY_400" :max-lines="PMaxLines.ONE" />
    </template>

    <template
      #selected-item="{
        index,
        opt,
        removeAtIndex,
      }: {
        index: number
        opt: string
        removeAtIndex(index: number): void
      }"
    >
      <chip :label="opt" :hide-remove="readonly" @remove="removeChip(index, removeAtIndex)" />
    </template>

    <template #no-option>
      <q-item>
        <div class="p-flex p-flex-center">
          <p-text-form-body :text="noData || $t('No items match the value')" />
        </div>
      </q-item>
    </template>
  </q-select>

  <mobile-suggestion-dialog v-model="isMobileDialogShow" show-confirm-buttons>
    <template #input>
      <p-text-input-field
        v-model="inputValue"
        end-icon="magnifying-glass"
        :placeholder="$t('Search')"
        :error-message="itemError"
        :debounce="debounce ?? 0"
        cy="search-input"
        @update:model-value="changeInputValue"
      />
    </template>
    <template #default>
      <slot name="options-header" />
      <q-virtual-scroll :items="mobileItems" separator class="p-flex-1">
        <template #before>
          <p-card v-if="canAddOwnText" :pa-mobile="PCardSize.SM" class="p-flex p-justify-center">
            <create-btn :text="$t('Add')" end-icon="plus" @click="addValue(inputValue)" />
          </p-card>

          <q-item v-if="mobileItems.length === 0 && !canAddOwnText">
            <div class="p-flex p-flex-center">
              <p-text-form-body :text="noData || $t('No items match the value')" />
            </div>
          </q-item>
        </template>

        <template #default="{ item }: { item: string }">
          <q-item clickable @click="toggleItem(item)">
            <div class="p-flex p-items-center p-flex-1">
              <p-checkbox
                :model-value="isSelected(item)"
                :label="item"
                :cy="`item-${item}`"
                @update:model-value="toggleItem(item)"
              />
            </div>
          </q-item>
        </template>
      </q-virtual-scroll>
    </template>
  </mobile-suggestion-dialog>
</template>
